import * as Yup from 'yup'

import { Form, FormHelpersProviderReturn, Clinic } from '../../../ana-toolbox'
import { createFields } from '../helpers/createFields'
import { shouldAllowAllPhoneTypes } from '../helpers/featureFlags'
import getAgeFromBirthday from '../helpers/getAgeFromBirthday'
import { buildValidationSchema } from '../yup/buildValidationSchema'
import { addressValidation, emailValidation, latinValidation, mobilePhoneValidation, phoneValidation } from '../yup/yup'

const EMAIL_ERROR = 'email_error'
const PHONE_ERROR = 'phone_error'
const AGE_VALIDATION = 70 // age group separator

Yup.addMethod<Yup.StringSchema>(Yup.string, 'address', addressValidation)
Yup.addMethod<Yup.StringSchema>(Yup.string, 'customEmail', emailValidation)
Yup.addMethod<Yup.StringSchema>(Yup.string, 'phoneNumber', phoneValidation)
Yup.addMethod<Yup.StringSchema>(Yup.string, 'mobilePhoneNumber', mobilePhoneValidation)
Yup.addMethod<Yup.StringSchema>(Yup.string, 'latin', latinValidation)

/* The e-mail address is never mandatory. Requiring it made patients without one share the same
 * fake address, which got clinic messages sent to the wrong person. It is still format checked
 * whenever it is filled in, and `getPhoneSchema` keeps asking for a phone number while it is
 * empty, so an anamnesis never ends up without any way to reach the patient.
 */
const emailSchema = Yup.string().latin().email(EMAIL_ERROR).customEmail(EMAIL_ERROR)

const getPhoneSchema = (form: Partial<Form>) => {
  const mobileRequiredSchema = Yup.string().mobilePhoneNumber(PHONE_ERROR).required(PHONE_ERROR)
  // Any type of phone number is allowed, not only mobile.
  const phoneRequiredSchema = Yup.string().phoneNumber(PHONE_ERROR).required(PHONE_ERROR)
  // Phone is not required
  const optionalSchema = Yup.string().phoneNumber(PHONE_ERROR)

  if (!form.birthday) return mobileRequiredSchema

  const age = getAgeFromBirthday(form.birthday)

  if (age < AGE_VALIDATION) return mobileRequiredSchema
  if (!form.email) return phoneRequiredSchema

  return optionalSchema
}

const getFieldWithRequiredValidationByAge = (dateOfBirth?: string, dependentValue?: string): boolean => {
  if (!dateOfBirth) return true

  const age = getAgeFromBirthday(dateOfBirth)

  if (age < AGE_VALIDATION) return true

  return !dependentValue
}

const createValidateConditionalSchema = (
  getSchema: (form: Partial<Form>) => Yup.StringSchema<string | null | undefined>
) => {
  return function (this: Yup.TestContext, value: string | null | undefined) {
    const form = (this.parent || {}) as Partial<Form>
    const schema = getSchema(form)

    try {
      schema.validateSync(value)

      return true
    } catch (e) {
      return this.createError({
        path: this.path,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        message: e.message,
      })
    }
  }
}

const fields = createFields({
  street: {
    __type: 'address',
    label: 'field.street_and_number',
    name: 'street',
    isRequired: true,
    inputProps: {
      autoFocus: true,
    },
    validation: Yup.string().latin().address('invalid_address_error').required('street_and_number_error'),
  },
  zipCode: {
    __type: 'text',
    label: 'field.zipCode',
    name: 'zipCode',
    isRequired: true,
    validation: Yup.string().latin().required('zipCode_error'),
    hooks: {
      beforeChange: (_value, helpers: FormHelpersProviderReturn, _event) => {
        helpers.setFieldValue('addressGoogleVerified', false)
      },
    },
  },
  city: {
    __type: 'text',
    label: 'field.city',
    name: 'city',
    isRequired: true,
    validation: Yup.string().latin().required('city_error'),
    hooks: {
      beforeChange: (_value, helpers: FormHelpersProviderReturn, _event) => {
        helpers.setFieldValue('addressGoogleVerified', false)
      },
    },
  },
  email: {
    __type: 'text',
    label: 'field.email',
    name: 'email',
    isRequired: false,
    inputProps: {
      type: 'email',
    },
    validation: emailSchema,
  },
  phone: {
    __type: 'phone',
    label: 'field.phone',
    name: 'phone',
    flagSource: (country: string) => {
      return `https://country-iso-flags.s3.eu-central-1.amazonaws.com/${(country.toLowerCase() === 'gg'
        ? 'gb'
        : country.toLowerCase()
      ).toLocaleLowerCase()}.svg`
    },
    isRequired: (form: Partial<Form>) => getFieldWithRequiredValidationByAge(form.birthday, form.email),
    inputProps: {
      type: 'tel',
    },
    validation: Yup.string().test('conditionalPhone', PHONE_ERROR, createValidateConditionalSchema(getPhoneSchema)),
  },
})

export const validationSchema = buildValidationSchema(fields)

/*
 * This function returns the validation schema based on the current clinic.
 * This is used to change the phone number validation for some clinics (see `src/helpers/featureFlags.ts`).
 * If the clinic allow all phone types, we override the default validation which only allows mobile phone numbers
 * with a validation that allow all types of phone numbers.
 */
export const getValidationSchemaForClinic = (clinic?: Pick<Clinic, 'referenceId'>) => {
  if (!clinic || !shouldAllowAllPhoneTypes(clinic)) return validationSchema

  return buildValidationSchema({
    ...fields,
    phone: {
      ...fields.phone,
      validation: Yup.string().phoneNumber('phone_error').required('phone_error'),
    },
  })
}

export default fields
